import {
  Component,
  forwardRef,
  Input,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';

/**
 * Inline time picker with independent hour (1–12) and minute (00–59) plain
 * text inputs and an AM/PM toggle.
 * Autocomplete removed — users type directly; values are clamped on blur.
 * Implements ControlValueAccessor — bound value is a 24-hour `HH:MM` string.
 */
@Component({
  selector: 'app-time-picker',
  templateUrl: './time-picker.component.html',
  styleUrls: ['./time-picker.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TimePickerComponent),
      multi: true,
    },
  ],
})
export class TimePickerComponent implements ControlValueAccessor {
  /** Label shown above the control. */
  @Input() label = 'Time';

  hour   = 9;    // 1–12 (12-hour)
  minute = 0;    // 0–59
  period: 'AM' | 'PM' = 'AM';

  /** Raw strings shown in the inputs while the user is typing. */
  hourDisplay   = '9';
  minuteDisplay = '00';

  disabled = false;

  private onChange: (v: string) => void = () => {};
  private onTouched: () => void = () => {};

  // ControlValueAccessor -------------------------------------------------

  writeValue(val: string): void {
    if (!val) return;
    const [hStr, mStr] = val.split(':');
    const h24 = parseInt(hStr, 10);
    this.minute = parseInt(mStr, 10) || 0;
    this.period = h24 < 12 ? 'AM' : 'PM';
    this.hour   = h24 % 12 || 12;
    this.hourDisplay   = String(this.hour);
    this.minuteDisplay = String(this.minute).padStart(2, '0');
  }

  registerOnChange(fn: (v: string) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void         { this.onTouched = fn; }
  setDisabledState(isDisabled: boolean): void     { this.disabled = isDisabled; }

  // Template handlers ----------------------------------------------------

  onHourInput(raw: string): void {
    this.hourDisplay = raw;
    const n = parseInt(raw, 10);
    if (!isNaN(n) && n >= 1 && n <= 12) {
      this.hour = n;
      this.emit();
    }
  }

  onHourBlur(): void {
    // Clamp to valid range and normalise display.
    const n = parseInt(this.hourDisplay, 10);
    if (isNaN(n) || n < 1 || n > 12) {
      // revert to last valid
      this.hourDisplay = String(this.hour);
    } else {
      this.hour = n;
      this.hourDisplay = String(n);
      this.emit();
    }
    this.onTouched();
  }

  onMinuteInput(raw: string): void {
    this.minuteDisplay = raw;
    const n = parseInt(raw, 10);
    if (!isNaN(n) && n >= 0 && n <= 59) {
      this.minute = n;
      this.emit();
    }
  }

  onMinuteBlur(): void {
    const n = parseInt(this.minuteDisplay, 10);
    if (isNaN(n) || n < 0 || n > 59) {
      this.minuteDisplay = String(this.minute).padStart(2, '0');
    } else {
      this.minute = n;
      this.minuteDisplay = String(n).padStart(2, '0');
      this.emit();
    }
    this.onTouched();
  }

  /** Allow only digits and control keys (backspace, arrows, tab, delete). */
  onSegmentKeydown(event: KeyboardEvent): void {
    const allowed = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'];
    if (!allowed.includes(event.key) && !/^\d$/.test(event.key)) {
      event.preventDefault();
    }
  }

  onPeriodChange(p: 'AM' | 'PM'): void {
    this.period = p;
    this.emit();
  }

  // Internal helpers -----------------------------------------------------

  private emit(): void {
    let h24 = this.hour % 12;             // 12 → 0, others unchanged
    if (this.period === 'PM') h24 += 12;  // PM: 0+12=12 … 11+12=23
    const value = `${String(h24).padStart(2, '0')}:${String(this.minute).padStart(2, '0')}`;
    this.onChange(value);
  }
}
